/**
 * Is the environment this admin panel is talking to actually able to run the
 * contract review flow?
 *
 * Why this exists: the review flow is spread across a database migration and an
 * edge-function deployment, and against an environment that is BEHIND, clicking
 * "Generate draft" does not fail safe — the older `create-contract` ignores the
 * `draft` flag, releases the contract and emails the tenant a signing link. The
 * client detects that afterwards (see describeStaleContractFunction) and says so
 * loudly, but by then the email has been sent. An alarm after the fact is not a
 * guard.
 *
 * So probe the one thing a browser CAN check without side effects: whether the
 * columns the flow depends on exist. Selecting a column a database does not have
 * fails the query (PostgREST 42703) and costs one cheap round trip. If they are
 * missing, the environment is a migration behind — which in every real deploy
 * also means the function is behind — and the destructive actions are disabled
 * with an explanation instead of being allowed to misfire.
 *
 * Deliberately precise about failure: ONLY a missing-column error counts as "not
 * ready". Any other error (offline, a transient 500) leaves the flow enabled —
 * blocking every admin from generating a contract because one probe hiccuped
 * would be a worse failure than the one this prevents, and the post-hoc alarm
 * still covers that case.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ContractFlowReadiness = {
  /** 20260803090000 — the verification trio that gates sending. */
  verification: boolean;
  /** 20260804091500 — admin_notes.kind, where the internal note now lives. */
  internalNote: boolean;
};

/**
 * True when this PostgREST error is specifically "that column isn't there".
 *
 * Exported so the discrimination is testable: everything about this guard hangs
 * on NOT treating an unrelated failure as "the environment is behind".
 */
export function isMissingColumn(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: string; message?: string };
  if (e.code === "42703" || e.code === "PGRST204") return true;
  const msg = String(e.message ?? "");
  return (
    /column .* does not exist/i.test(msg) ||
    /could not find the '.*' column/i.test(msg)
  );
}

export function useContractFlowReady(enabled: boolean = true) {
  return useQuery<ContractFlowReadiness>({
    queryKey: ["v2", "contract-flow-ready"],
    enabled,
    // Schema does not change often, but after a deploy the panel should notice
    // without a hard reload — a minute is short enough to self-heal and long
    // enough not to re-probe on every profile open.
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const [verification, internalNote] = await Promise.all([
        supabase.from("bookings").select("contract_draft_verified_at").limit(1),
        supabase.from("admin_notes").select("kind").limit(1),
      ]);
      return {
        verification: !isMissingColumn(verification.error),
        internalNote: !isMissingColumn(internalNote.error),
      };
    },
  });
}

/** The two commands that bring an environment up to date, in the order they must run. */
export const CONTRACT_DEPLOY_STEPS = [
  "supabase db push",
  "supabase functions deploy create-contract",
] as const;
